/**
 * Ranking KPI cell shared by Sportsbook / Casino top-* analytics endpoints.
 * BE returns either a bare number (legacy / by-state) or the KPI-card envelope
 * `{ value, previous_value, percent_change }` (top-sports / top-providers / …).
 */

export type RankingKpiMetric = {
  value: number;
  percent_change: number | null;
};

export const DEFAULT_TOP_RANKING_LIMIT = 10;

/** Allowed `limit` values for top ranking graphs (BE accepts 1–50). */
export const TOP_RANKING_LIMIT_OPTIONS = [5, 10, 15, 20, 25, 50] as const;
export const TOP_RANKING_MIN_CHART_HEIGHT_PX = 320;
export const TOP_RANKING_BAR_ROW_HEIGHT_PX = 32;
export const TOP_RANKING_SCROLL_THRESHOLD = 15;

export type TopRankingLimit = (typeof TOP_RANKING_LIMIT_OPTIONS)[number];

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/** True when a raw field is a bare number or a `{ value }` envelope. */
export function isRankingMetricField(value: unknown): boolean {
  if (isFiniteNumber(value)) return true;
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  return isFiniteNumber((value as { value?: unknown }).value);
}

/**
 * Unwrap a ranking metric field. Bare numbers keep `percent_change: null`
 * (no change badge). Envelope `percent_change` of `null`/non-finite stays null.
 */
export function parseRankingKpiMetric(raw: unknown): RankingKpiMetric {
  if (isFiniteNumber(raw)) {
    return { value: raw, percent_change: null };
  }
  if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) {
    const entry = raw as { value?: unknown; percent_change?: unknown };
    const value = isFiniteNumber(entry.value) ? entry.value : 0;
    const percent_change = isFiniteNumber(entry.percent_change)
      ? entry.percent_change
      : null;
    return { value, percent_change };
  }
  return { value: 0, percent_change: null };
}
